import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Api from "../../axiosConfig";
import {
  FaTruck,
  FaBox,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaFileDownload,
  FaPrint,
  FaQrcode
} from "react-icons/fa";

const DeliveryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState(null);

  useEffect(() => {
    const fetchDelivery = async () => {
      try {
        const response = await Api.get(`/v1/deliveries/${id}`);
     
        setDelivery(response.data.delivery);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch delivery details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDelivery();
    }
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "assigned":
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const generateLabel = async () => {
    try {
      setLabelLoading(true);
      setLabelError(null);
      const response = await Api.get(`/v1/deliveries/${id}/label`);
      
      if (response.data.success && response.data.data) {
        // Update delivery state with new label data
        setDelivery(prev => ({
          ...prev,
          shippingLabel: response.data.data
        }));
      }
    } catch (err) {
      setLabelError(err.response?.data?.message || 'Failed to generate shipping label');
    } finally {
      setLabelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900">Loading...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900">Error</h2>
        <p className="mt-2 text-gray-600">{error}</p>
        <Link
          to="/deliveries"
          className="mt-4 inline-block px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-light transition-colors"
        >
          Back to Deliveries
        </Link>
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900">
          Delivery Not Found
        </h2>
        <p className="mt-2 text-gray-600">
          The delivery you're looking for doesn't exist.
        </p>
        <Link
          to="/deliveries"
          className="mt-4 inline-block px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-light transition-colors"
        >
          Back to Deliveries
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Delivery #{delivery.orderId}
        </h1>
        <Link
          to="/deliveries"
          className="text-primary-dark hover:text-primary-light transition-colors"
        >
          ← Back to Deliveries
        </Link>
      </div>

      {/* Status and Creation Date */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap justify-between items-center">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span
              className={`mt-1 px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getStatusColor(
                delivery.status
              )}`}
            >
              {delivery.status || 'N/A'}
            </span>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Created At</p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {formatDate(delivery.createdAt)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recipient Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <FaUser className="w-5 h-5 text-primary-dark mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Recipient Details
            </h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <FaUser className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">
                  {delivery.recipientName || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <FaPhone className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">
                  {delivery.recipientPhone || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <FaEnvelope className="w-4 h-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">
                  {delivery.recipientEmail || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Package Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <FaBox className="w-5 h-5 text-primary-dark mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Package Details
            </h2>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Type</p>
              <p className="text-sm font-medium text-gray-900 capitalize">
                {delivery.packageType || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Size</p>
              <p className="text-sm font-medium text-gray-900 capitalize">
                {delivery.packageSize ? delivery.packageSize.replace(/_/g, " ") : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Delivery Type</p>
              <p className="text-sm font-medium text-gray-900 capitalize">
                {delivery.deliveryType ? delivery.deliveryType.replace(/-/g, " ") : 'N/A'}
              </p>
            </div>
            {delivery.specialInstructions && (
              <div>
                <p className="text-sm text-gray-500">Special Instructions</p>
                <p className="text-sm font-medium text-gray-900">
                  {delivery.specialInstructions}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Pickup Address */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <FaMapMarkerAlt className="w-5 h-5 text-primary-dark mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Pickup Address
            </h2>
          </div>
          <p className="text-sm text-gray-900">{delivery.pickupAddress || 'N/A'}</p>
        </div>

        {/* Delivery Address */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <FaTruck className="w-5 h-5 text-primary-dark mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Delivery Address
            </h2>
          </div>
          <p className="text-sm text-gray-900">{delivery.deliveryAddress || 'N/A'}</p>
        </div>

        {/* Shipping Label */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FaQrcode className="w-5 h-5 text-primary-dark mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                Shipping Label
              </h2>
            </div>
            {!delivery.shippingLabel && (
              <button
                onClick={generateLabel}
                disabled={labelLoading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-dark hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark ${
                  labelLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {labelLoading ? 'Generating...' : 'Generate Label'}
              </button>
            )}
          </div>

          {labelError && (
            <div className="text-sm text-red-600 mb-4">{labelError}</div>
          )}

          {delivery.shippingLabel && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <a
                  href={delivery.shippingLabel.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 border-solid text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark"
                >
                  <FaFileDownload className="mr-2" />
                  Download PDF
                </a>
                <a
                  href={delivery.shippingLabel.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 border-solid text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark"
                >
                  <FaPrint className="mr-2" />
                  Print Label
                </a>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tracking Code</p>
                <p className="text-sm font-medium text-gray-900">
                  {delivery.shippingLabel.trackingCode}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Generated At</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(delivery.shippingLabel.generatedAt)}
                </p>
              </div>
              <div className="mt-4">
                <img
                  src={delivery.shippingLabel.imageUrl}
                  alt="Shipping Label"
                  className="max-w-full h-auto rounded-lg border border-gray-200 border-solid border-solidr"
                />
              </div>
            </div>
          )}
        </div>

        {/* Package Image */}
        {delivery.packageImage && (
          <div className="md:col-span-2 bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-4">
              <FaBox className="w-5 h-5 text-primary-dark mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                Package Image
              </h2>
            </div>
            <div className="mt-2">
              <img
                src={delivery.packageImage}
                alt="Package"
                className="max-w-full h-auto rounded-lg"
              />
            </div>
          </div>
        )}

        {delivery.pod && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-4">
              <FaQrcode className="w-5 h-5 text-primary-dark mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                Proof of Delivery
              </h2>
            </div>
            <div className="mt-2">
              <img
                src={delivery.pod.photo}
                alt="Proof of Delivery"
                className="max-w-full h-auto rounded-lg"
              />
              <p className="text-sm text-gray-500 mt-2">
                Delivered at: {formatDate(delivery.pod.timestamp)}
              </p>
              {delivery.pod.notes && (
                <p className="text-sm text-gray-600 mt-2">
                  Notes: {delivery.pod.notes}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryDetails;
